import type { BaseBusy, BaseEnvelope } from './base-patch';

/** Conflicts, busy SELECTs and incomplete envelopes share a short foreground budget.
 * After it, keep the adoption pending and follow fresh SELECTs in the background.
 * Catalog lag is recoverable; it never certifies a partial envelope or refuses a surface. */
export const CATALOG_RECOVERY_REREADS = 3;

export async function recoverBaseCatalog(
    envelope: BaseEnvelope | BaseBusy,
    hydrate: (envelope: BaseEnvelope | BaseBusy) => Promise<BaseEnvelope | BaseBusy>,
    load: (id: string, entity: string) => Promise<BaseEnvelope | BaseBusy>,
    rereads = CATALOG_RECOVERY_REREADS,
    wait = waitForCatalogProgress,
): Promise<BaseEnvelope | BaseBusy> {
    let current = envelope;
    let remaining = rereads;
    let conflict: unknown;
    let background = 0;
    for (;;) {
        if (!('resync' in current) && (current.metadata || current.catalog_version)) {
            try { return await hydrate(current); }
            catch (error) {
                if (!(error instanceof Error) || error.message !== 'inventory-catalog:409') throw error;
                conflict = error;
            }
        }
        // Follow the envelope returned by each confirming SELECT, including its
        // current catalog version and ref; never restart with the original ref.
        do {
            if (remaining-- <= 0) {
                if (background++ === 0) reportCatalogRecovery(current, conflict);
                await wait(Math.min(1000 * 2 ** Math.min(background - 1, 4), 15000));
            }
            current = await load(envelope.entity_id, envelope.entity);
            if ('resync' in current && remaining > 0) await new Promise(resolve => setTimeout(resolve, 50));
        } while ('resync' in current);
    }
}

/** The catalog batch owner calls this only after splitting a 409 to a single ref.
 * Preserve the server's comparison, rather than reducing it to an HTTP status. */
export async function catalogReadError(
    response: Pick<Response, 'status' | 'json'>,
    requestedVersion: string,
    requestedRefs: readonly string[],
): Promise<Error> {
    if (response.status !== 409) return new Error(`inventory-catalog:${response.status}`);
    let body: unknown;
    try { body = await response.json(); } catch { /* Recovery still follows a body-less conflict. */ }
    const error = new Error('inventory-catalog:409');
    return Object.assign(error, { detail: { requested_version: requestedVersion, requested_refs: [...requestedRefs], response: body } });
}

function reportCatalogRecovery(envelope: BaseEnvelope | BaseBusy, conflict: unknown): void {
    if (typeof window === 'undefined' || typeof CustomEvent !== 'function') return;
    const detail = typeof conflict === 'object' && conflict !== null && 'detail' in conflict ? conflict.detail : undefined;
    // Diagnostic only: no displayWarning and no saved skip row. `slow` is an
    // existing telemetry kind; the surface's adoption remains recoverable.
    window.dispatchEvent(new CustomEvent('ft:unported', { detail: {
        reason: 'catalog:background-recovery', mode: 'slow', detail: {
            entityId: envelope.entity_id, entity: envelope.entity,
            ...!('resync' in envelope) ? { requested_version: envelope.catalog_version, requested_ref: envelope.base.catalog_ref } : {},
            conflict: detail,
        },
    } }));
}

/** One read per wake after the foreground budget, with a timer backstop when no
 * peer frame arrives. Remove every listener when either path wins. */
function waitForCatalogProgress(ms: number): Promise<void> {
    return new Promise(resolve => {
        const events = ['ft:ws-state-frame', 'ft:truth-frame-applied', 'ft:entity-base-compiled', 'ft:inventory-base-compiled'];
        const finish = (): void => {
            clearTimeout(timer);
            if (typeof window !== 'undefined') for (const event of events) window.removeEventListener(event, finish);
            resolve();
        };
        const timer = setTimeout(finish, ms);
        if (typeof window !== 'undefined') for (const event of events) window.addEventListener(event, finish, { once: true });
    });
}
